import * as RToast from '@radix-ui/react-toast';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useChromeDirection } from '@/lib/i18n/useLocale';
import { cn } from './cn';

type ToastTone = 'info' | 'success' | 'warning' | 'error';

interface ToastItem {
  id: number;
  title: string;
  description?: ReactNode;
  tone: ToastTone;
  /** request_id surfaced for support correlation (spec §5.1 / §17). */
  requestId?: string;
}

interface ToastApi {
  toast: (t: Omit<ToastItem, 'id'>) => void;
  success: (title: string, description?: ReactNode) => void;
  error: (title: string, opts?: { description?: ReactNode; requestId?: string }) => void;
  info: (title: string, description?: ReactNode) => void;
  warning: (title: string, description?: ReactNode) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const toneClass: Record<ToastTone, string> = {
  info: 'border-accent-blue',
  success: 'border-signal-teal',
  warning: 'border-signal-amber',
  error: 'border-signal-error',
};

let seq = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  // Swipe-to-dismiss goes towards the inline end, which is the left edge in Arabic.
  const dir = useChromeDirection();
  const [items, setItems] = useState<ToastItem[]>([]);

  const push = useCallback((t: Omit<ToastItem, 'id'>) => {
    setItems((prev) => [...prev, { ...t, id: ++seq }]);
  }, []);

  // A stable API object: callers put `toast` (or a callback closing over it, like
  // `useApiError`) in effect and memo dependencies, and a fresh object per render would
  // re-fire those effects on every toast that is shown or dismissed.
  const api = useMemo<ToastApi>(
    () => ({
      toast: push,
      success: (title, description) => push({ title, description, tone: 'success' }),
      error: (title, opts) =>
        push({ title, description: opts?.description, tone: 'error', requestId: opts?.requestId }),
      info: (title, description) => push({ title, description, tone: 'info' }),
      warning: (title, description) => push({ title, description, tone: 'warning' }),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      <RToast.Provider swipeDirection={dir === 'rtl' ? 'left' : 'right'} duration={6000}>
        {children}
        {items.map((item) => (
          <RToast.Root
            key={item.id}
            onOpenChange={(open) => {
              if (!open) setItems((prev) => prev.filter((x) => x.id !== item.id));
            }}
            className={cn(
              // `pointer-events-auto` re-enables the toast itself inside the click-through
              // viewport; `pe-2xl` keeps the text clear of the dismiss target in the corner.
              // The toast's own shadow would override the base focus ring, so it is
              // restated for focus.
              'data-[state=open]:animate-in data-[state=closed]:animate-out pointer-events-auto relative flex flex-col gap-[2px] rounded-md border-s-[3px] border-[0.5px] border-hairline bg-canvas-paper p-md pe-2xl shadow-nav-scrolled focus-visible:shadow-focus',
              toneClass[item.tone],
            )}
          >
            <RToast.Title className="text-small font-medium text-ink-deep">{item.title}</RToast.Title>
            {item.description && (
              <RToast.Description className="text-small text-ink-mid">
                {item.description}
              </RToast.Description>
            )}
            {item.requestId && (
              <details className="mt-2xs">
                <summary className="cursor-pointer text-label text-ink-mute">{t('ui.reportThis')}</summary>
                <code className="tnum mt-2xs block select-all font-mono text-label text-ink-mute">
                  {item.requestId}
                </code>
              </details>
            )}
            {/* Toasts land bottom-end, which is where Save lives on every editor, so waiting
                out the 6 s timer is not an option. The target is the house 44 px (the whole
                corner); the visible mark is 24 px and carries the focus ring. */}
            <RToast.Close
              aria-label={t('ui.dismissNotification')}
              className="group absolute end-0 top-0 inline-flex h-[44px] w-[44px] items-center justify-center rounded-md text-ink-mid focus-visible:shadow-none"
            >
              <span
                aria-hidden
                className="inline-flex h-[24px] w-[24px] items-center justify-center rounded-sm transition-colors duration-almanac group-hover:bg-canvas-deep-sand group-hover:text-ink-deep group-focus-visible:shadow-focus"
              >
                ✕
              </span>
            </RToast.Close>
          </RToast.Root>
        ))}
        {/* The viewport is a fixed 380 px box even when empty, so without
            `pointer-events-none` it swallows clicks on the buttons underneath it. Each
            toast opts back in. */}
        <RToast.Viewport className="pointer-events-none fixed bottom-0 end-0 z-[100] flex w-[380px] max-w-[100vw] flex-col gap-sm p-lg" />
      </RToast.Provider>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
}
